import { Container, Spacer, Tabs } from '@chakra-ui/react'
import { useState, useEffect, useMemo } from 'react'
import { LuSettings } from 'react-icons/lu'
import { PiClockCounterClockwise, PiPresentationLight, PiSoccerBallLight } from 'react-icons/pi'
import { Link, useParams } from 'react-router-dom'

import useGetObject from '@app/hooks/useGetObject'
import useGetObjectsByProperties from '@app/hooks/useGetObjectsByProperties'
import AiDashboardPromptPlayground from '@app/pages/aiDashboard/components/aiDashboardPromptPlayground'
import AiDashboardReport from '@app/pages/aiDashboard/components/aiDashboardReport'
import AiDashboardReportHeader from '@app/pages/aiDashboard/components/aiDashboardReportHeader'
import AiDashboardReportHistory from '@app/pages/aiDashboard/components/aiDashboardReportHistory'
import AiDashboardReportSettings from '@app/pages/aiDashboard/components/aiDashboardReportSettings'
import type { DashboardItem } from '@app/pages/aiDashboard/types'
import Can from '@app/shared/authorization/can'

const Show = () => {
  const { recurringReportId, strategyId, tabId } = useParams()
  const [tabValue, setTabValue] = useState<string>(tabId || 'current')
  const recurringReportConfiguration = useGetObject(recurringReportId, 'recurringReportConfiguration') as DashboardItem
  const schedule = useGetObjectsByProperties('schedule', {
    schedulableType: 'RecurringReportConfiguration',
    schedulableId: recurringReportConfiguration?.id
  })?.[0]

  useEffect(() => {
    if (tabId) {
      setTabValue(tabId)
    }
  }, [tabId])

  const reports = useGetObjectsByProperties('report', {
    strategyId,
    recurringReportConfigurationId: recurringReportId
  })

  const dateSortedReports = useMemo(
    () => (reports || []).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [reports]
  )
  const mostRecentReport = dateSortedReports[0]
  const history = useMemo(() => dateSortedReports.slice(1), [dateSortedReports])

  return (
    <Container maxW="6xl" p={6} overflowY="auto">
      <AiDashboardReportHeader recurringReportConfiguration={recurringReportConfiguration} schedule={schedule} />
      <Tabs.Root lazyMount unmountOnExit size="lg" value={tabValue} onValueChange={(e) => setTabValue(e.value)} pt={4}>
        <Tabs.List>
          <Tabs.Trigger value="current" asChild>
            <Link to={`/strategy/${strategyId}/dashboard/${recurringReportId}/current`}>
              <PiPresentationLight /> Current
            </Link>
          </Tabs.Trigger>
          <Can I="update" an="strategy">
            <Tabs.Trigger value="playground" asChild>
              <Link to={`/strategy/${strategyId}/dashboard/${recurringReportId}/playground`}>
                <PiSoccerBallLight /> Prompt editor
              </Link>
            </Tabs.Trigger>
          </Can>
          <Tabs.Trigger value="history" asChild>
            <Link to={`/strategy/${strategyId}/dashboard/${recurringReportId}/history`}>
              <PiClockCounterClockwise /> Historical
            </Link>
          </Tabs.Trigger>
          <Can I="update" an="strategy">
            <Tabs.Trigger value="settings" asChild>
              <Link to={`/strategy/${strategyId}/dashboard/${recurringReportId}/settings`}>
                <LuSettings /> Settings
              </Link>
            </Tabs.Trigger>
          </Can>
          <Spacer />
        </Tabs.List>
        <Tabs.Content value="current">
          <AiDashboardReport report={mostRecentReport} />
        </Tabs.Content>

        <Can I="update" an="strategy">
          <Tabs.Content value="history">
            <AiDashboardReportHistory history={history} />
          </Tabs.Content>
        </Can>
        <Tabs.Content value="playground">
          <AiDashboardPromptPlayground recurringReportConfiguration={recurringReportConfiguration} />
        </Tabs.Content>
        <Can I="update" an="strategy">
          <Tabs.Content value="settings">
            <AiDashboardReportSettings
              // Keep the key. It's how we force a form reset when changing reports in the nav.
              // Forced reset is required due to our use of `defaultValue`. https://stackoverflow.com/a/21750576
              key={`${recurringReportConfiguration?.id},${schedule?.id}`}
              recurringReportConfiguration={recurringReportConfiguration}
              schedule={schedule}
            />
          </Tabs.Content>
        </Can>
      </Tabs.Root>
    </Container>
  )
}

export default Show
